const onlyZeroesRegex = /^0+$/

// cache the formatters for optimal performance (see https://v8.dev/blog/v8-release-76#localized-bigint)
const formatterCache: { [key: string]: Intl.NumberFormat } = {}

export function formatPrice(
    price: number,
    currencyCode: string,
    locale?: string,
    includeCurrencyCode: boolean = true
): string {
    let formattedNumber: string
    try {
        const cacheKey = `${locale}.${currencyCode}`
        let formatter = formatterCache[cacheKey]
        if (!formatter) {
            formatter = new Intl.NumberFormat(locale, {
                style: 'currency',
                currency: currencyCode,
                currencyDisplay: 'code',
            })
            formatterCache[cacheKey] = formatter
        }

        // For some locales (including en-US), it defaults to 'USD <amount>' but for
        // others it's '<amount> USD', so we need a regex to find the formatted number
        // without the currency code. We want to always show it as '<amount> USD'.
        const matches = formatter.format(price).match(/\d[\d\W_]+/)
        if (!matches || !matches[0]) {
            if (process.env.NODE_ENV === 'development') {
                throw Error('Error parsing price amount')
            }
            return ''
        }

        const amountString = matches[0].trimEnd()

        const decimalSeparator = new Intl.NumberFormat(locale)
            .format(1.1)
            .charAt(1)

        const [wholeUnits, fractionalUnits] =
            amountString.split(decimalSeparator)
        // if there are only zeroes after the decimal point, strip them out
        formattedNumber = onlyZeroesRegex.test(fractionalUnits)
            ? wholeUnits
            : amountString
    } catch (e) {
        const isUnrecognizedCurrency = e instanceof RangeError
        if (isUnrecognizedCurrency) {
            // if `isUnrecognizedCurrency` is true, then it's either cryptocurrency
            // or an obsolete currency that Intl.NumberFormat doesn't recognize
            const cacheKey = `${locale}.crypto`
            let formatter = formatterCache[cacheKey]
            if (!formatter) {
                formatter = new Intl.NumberFormat(locale, {
                    maximumFractionDigits: 10,
                })
                formatterCache[cacheKey] = formatter
            }
            formattedNumber = formatter.format(price)
        } else {
            console.warn(e)
            return ''
        }
    }

    return includeCurrencyCode
        ? `${formattedNumber} ${currencyCode}`
        : formattedNumber
}
